import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import useFormatter from '../../common/useFormatter';
import TitleSection from '../../components/layout/TitleSection';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';
import {
  analyzeGateUsageScheduleCustomThresholds,
  analyzeGateUsageScheduleWaitTimeCriticalThreshold,
  analyzeGateUsageScheduleWaitTimeLateThreshold,
  analyzeGateUsageScheduleWaitTimeWarningThreshold,
} from '../store/analyze.state';
import { WaitTimeClassificationModel } from './waitTimeClassificationModel';

export type WaitTimeClassificationLegendProps = {
  className?: string;
  showStatistics?: boolean;
  waitTimes?: number[]; // Optional: for showing statistics
};

const WaitTimeClassificationLegend: React.FC<
  WaitTimeClassificationLegendProps
> = props => {
  const { t } = useTranslation('simulation');
  const formatter = useFormatter();

  const warningThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeWarningThreshold,
  );
  const lateThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeLateThreshold,
  );
  const criticalThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeCriticalThreshold,
  );
  const customThresholds = useRecoilValue(
    analyzeGateUsageScheduleCustomThresholds,
  );

  const classificationModel = new WaitTimeClassificationModel(
    warningThreshold,
    lateThreshold,
    criticalThreshold,
    customThresholds,
  );

  const thresholds = classificationModel.getAllThresholds();
  const statistics = props.waitTimes
    ? classificationModel.getStatistics(props.waitTimes)
    : null;

  const formatTime = (seconds: number): string => {
    if (seconds === 0) return '0 min';
    if (seconds === Infinity) return '∞';
    return formatter.formatTimespanAs24String(seconds).fullString;
  };

  const getRangeText = (threshold: any): string => {
    if (threshold.id === 'on-time') {
      return '0 min';
    } else if (threshold.id === 'critically-late') {
      return `> ${formatTime(threshold.minSeconds)}`;
    } else {
      return `${formatTime(threshold.minSeconds)} - ${formatTime(threshold.maxSeconds)}`;
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'none':
        return '✓';
      case 'low':
        return '⚠';
      case 'medium':
        return '⚠';
      case 'high':
        return '⚠';
      case 'critical':
        return '⚠';
      default:
        return '•';
    }
  };

  return (
    <TitleSection
      key="dock-wait-time-stats"
      id="dock-wait-time-stats"
      title={t`Wait Time Stats`}
      inPanelView
      collapsible
      className={` ${props.className || ''}`}
    >
      <StatGroup
        title={t('Total Jobs: {{total}}', { total: statistics.total })}
        classNameStatGroupItems="flex gap-2 !space-x-0 px-2"
        classNameTitle="text-menu-text"
        horizontal
      >
        {thresholds.map(threshold => (
          <Stat
            key={threshold.id}
            title={threshold.label}
            titleAlt={threshold.description}
            value={statistics ? statistics.byStatus[threshold.id] : '—'}
            valuePercent={
              statistics
                ? {
                    value: `${statistics.percentages[threshold.id].toFixed(1)}`,
                    fullString: `${statistics.percentages[threshold.id].toFixed(1)}%`,
                    unit: '%',
                    raw: statistics.percentages[threshold.id],
                    unitCode: '%',
                  }
                : undefined
            }
            legendColor={threshold.timelineColors}
            className="p-2"
            inPanelMode
            isActionable
            isSelectable
            isPreview
            isFullWidth
          >
            <div className="flex flex-col gap-2 text-xs opacity-0 group-hover:opacity-100">
              <div className="flex items-center gap-2">
                <span className="text-lg">
                  {getSeverityIcon(threshold.severity)}
                </span>
                <span className="text-menu-text/80 font-medium">
                  {getRangeText(threshold)}
                </span>
              </div>

              {/* <div className="opacity-0 group-hover:opacity-100">
                {threshold.description}
              </div> */}

              {/* <div className="text-menu-text/60 text-xs">
                  <strong>Action:</strong> {threshold.actionRequired}
                </div> */}

              {/* {threshold.severity !== 'none' && (
                  <div className="flex items-center gap-1">
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: threshold.timelineColors[0] }}
                    />
                    <span className="text-xs font-medium uppercase">
                      {threshold.severity}
                    </span>
                  </div>
                )} */}
            </div>
          </Stat>
        ))}
      </StatGroup>

      {/* {statistics && (
          <div className="mt-4 flex justify-center">
            <Stat
              title={t`Total Jobs`}
              value={statistics.total}
              unitOfMeasure="jobs"
              className="bg-menu/10 border-menu/20 rounded border"
              cardView
              transparent={false}
            />
          </div>
        )} */}
    </TitleSection>
  );
};

export default WaitTimeClassificationLegend;
